import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';

import type { FieldDefinition, ValuesMode, WizardState } from './types';

interface ValuesStepProps {
  state: WizardState;
  updateState: (patch: Partial<WizardState>) => void;
  identifiers: FieldDefinition[];
}

const ValuesStep = ({ state, updateState, identifiers }: ValuesStepProps) => {
  const toggleIdentifier = useCallback(
    (key: string) => {
      const current = state.selectedIdentifiers;
      const next = current.includes(key) ? current.filter(i => i !== key) : [...current, key];
      updateState({ selectedIdentifiers: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.selectedIdentifiers, updateState],
  );

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        How should the update columns be pre-filled?
      </Typography>

      <RadioGroup
        value={state.valuesMode}
        onChange={(_e, value) => updateState({ valuesMode: value as ValuesMode })}
      >
        <FormControlLabel value="prefill" control={<Radio />} label="Pre-fill with the current catalog values" />
        <FormControlLabel value="blank" control={<Radio />} label="Leave blank — I'll enter the new values myself" />
      </RadioGroup>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Identifier columns are always pre-filled so rows match existing records.
      </Typography>

      {identifiers.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Which external identifiers would you like to include in your template?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Optional. Select the external catalog identifier(s) that you would like to appear in your template file.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {identifiers.map(identifier => (
              <FormControlLabel
                key={identifier.key}
                control={(
                  <Checkbox
                    checked={state.selectedIdentifiers.includes(identifier.key)}
                    onChange={() => toggleIdentifier(identifier.key)}
                  />
                )}
                label={identifier.label}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ValuesStep;
